/**
 * WAITING and LANDED.
 *
 * Two honesty rules live here:
 *
 * 1. A chase is a follow-up on the existing ask, never a new one. "Asked once,
 *    chased three times" is the truth; "asked four times" is not, and it would
 *    quietly inflate the one metric that is genuinely the rep's.
 *
 * 2. A public review that nobody has opened is labelled SELF-REPORTED on its
 *    own row. Folding it into the same total as a verified one is how a number
 *    stops being worth reading.
 */
import { useState } from 'react'
import s from './reviews.module.css'

const TYPE_LABEL = { internal: 'Internal', google: 'Google', facebook: 'Facebook' }

export default function ReviewList({ data, filter, canVerify, onChase, onReceived, onVerify }) {
  const [busy, setBusy] = useState(null)
  const rows = data?.reviews || []

  const run = async (key, fn) => { setBusy(key); try { await fn() } finally { setBusy(null) } }

  const recordLanded = (r) => {
    const payload = {}
    if (r.type === 'internal') {
      const content = window.prompt(
        'Paste the testimonial. SR owns these words, so the text IS the asset:'
      )
      if (!content) return
      payload.content = content
      const who = window.prompt('Who said it? (name and role — optional)')
      if (who) payload.authorName = who
    } else {
      const url = window.prompt(`Paste the link to the ${TYPE_LABEL[r.type]} review:`)
      if (!url) return
      payload.url = url
    }
    run('recv' + r.id, () => onReceived(r.id, payload))
  }

  if (!rows.length) {
    return (
      <div className={s.wrap}>
        <div className={s.empty}>
          {filter === 'landed'
            ? 'Nothing has landed yet.'
            : 'Nothing asked and waiting. Anything you ask for from the queue appears here until it lands.'}
        </div>
      </div>
    )
  }

  return (
    <div className={s.wrap}>
      <div className={s.list}>
        {rows.map((r) => (
          <article key={r.id} className={s.card}>
            <div className={s.head}>
              <div>
                <div className={s.account}>{r.account}</div>
                <div className={s.meta}>
                  <span className={s.chip}>{TYPE_LABEL[r.type] || r.type}</span>
                  {r.sport && <span>{r.sport}</span>}
                  {r.requestedByName && <span>· asked by {r.requestedByName}</span>}
                  {r.askLagDays != null && (
                    <span>· asked {r.askLagDays}d after delivery</span>
                  )}
                  {r.followups > 0 && <span>· chased {r.followups}×</span>}
                </div>
              </div>
              <div className={s.when}>
                {filter === 'landed' ? (
                  <>
                    <div className={s.days}>{String(r.receivedAt || '').slice(0, 10)}</div>
                    <div className={s.whenLabel}>Landed</div>
                  </>
                ) : (
                  <>
                    <div className={s.days}>{r.daysWaiting ?? 0}d</div>
                    <div className={s.whenLabel}>Waiting</div>
                  </>
                )}
              </div>
            </div>

            {r.content && <div className={s.guidance}>“{r.content}”</div>}

            {filter === 'landed' && (
              <div className={s.meta} style={{ marginTop: 10 }}>
                {r.type === 'internal' ? (
                  <span className={s.ok}>Text held — self-proving</span>
                ) : r.verifiedAt ? (
                  <span className={s.ok}>Verified by a second person</span>
                ) : (
                  <span className={s.selfReported}>
                    Self-reported — nobody has opened the link
                  </span>
                )}
                {r.url && (
                  <a href={r.url} target="_blank" rel="noopener noreferrer">open</a>
                )}
              </div>
            )}

            <div className={s.actions}>
              {filter === 'outstanding' && (
                <>
                  <button
                    type="button"
                    className={s.btn}
                    disabled={busy === 'recv' + r.id}
                    onClick={() => recordLanded(r)}
                  >
                    It landed
                  </button>
                  <button
                    type="button"
                    className={`${s.btn} ${s.ghost}`}
                    disabled={busy === 'chase' + r.id}
                    onClick={() => run('chase' + r.id, () => onChase(r.id))}
                  >
                    Chased again
                  </button>
                </>
              )}
              {filter === 'landed' && r.type !== 'internal' && !r.verifiedAt && canVerify && (
                <button
                  type="button"
                  className={s.btn}
                  disabled={busy === 'ver' + r.id}
                  onClick={() => run('ver' + r.id, () => onVerify(r.id))}
                >
                  I opened it — verified
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <p className={s.note}>
        {filter === 'outstanding'
          ? 'A polite second ask is legitimate. A fourth is noise. The waiting column is what tells them apart — and a chase never increments the ask count.'
          : data?.caveat ||
            'Self-reported means somebody ticked a box and nobody opened the link. It is kept as its own state rather than folded into the total.'}
      </p>
    </div>
  )
}
